<template>
	<el-dialog
		:modelValue="open"
		@update:modelValue="$emit('update:open', $event)"
		:title="'Einem Fall zuordnen'"
		@close="handleClose"
		:before-close="(done) => dialogBeforeClose(done, waiting)"
	>
		<TheForm
			:model="request"
			:rules="rules"
			:waiting="waiting"
			:waitingMsg="texts.formErrors.waiting"
			:showErr="showErr"
			:errMsg="errMsg"
			ref="formRef"
			:id="id"
		>
			<el-form-item label="Status">
				<el-radio-group v-model="state">
					<el-radio :value="'active'">Aktiv</el-radio>
					<el-radio :value="'done'">Abgeschlossen (Entscheidung)</el-radio>
				</el-radio-group>
			</el-form-item>

			<el-form-item label="Zeitraum" prop="timePeriod">
				<el-checkbox-group v-model="selectedTimePeriod" placeholder="Alle">
					<el-checkbox
						v-for="item in timePeriodOptions"
						:key="item.value"
						:value="item.value"
						:border="true"
						@change="() => handleClickOptionWrapper(item)"
					>
						{{ item.label }}
					</el-checkbox>
				</el-checkbox-group>
			</el-form-item>

			<el-form-item label="Fall wählen" prop="searchTerm">
				<el-select
					v-model="request.searchTerm"
					filterable
					remote
					clearable
					placeholder="Fallnamen schreiben"
					remote-show-suffix
					:remote-method="doSearch"
					:loading="casesLoading"
					@change="setCaseId"
					@clear="caseId = null"
					@focus="focusRemoteSearchSelect"
				>
					<el-option v-for="item in filteredCases" :key="item.value" :label="item.label" :value="item.value" />
				</el-select>
			</el-form-item>
		</TheForm>

		<template #footer>
			<div class="button-panel">
				<TheButton type="danger" @buttonclick="save" :disabled="!caseId || waiting" :loading="waiting">Zuordnen</TheButton>
				<TheButton type="default" @buttonclick="handleClose">Abbrechen</TheButton>
			</div>
		</template>
	</el-dialog>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue';
import TheForm from '../TheForm.vue';
import TheButton from '../TheButton.vue';
import { AssignCaseToComplaintRequest, CaseState } from '../../types/types';
import { texts, translateToGerman } from '../../utils/texts';
import { returnFormOptions } from '../../utils/formOptions.js';
import type { FormInstance } from 'element-plus';
import { assignCaseToComplaint } from '@/requests/complaints';
import { prepareSearchRequest, getDateRangeFromTimePeriod, handleClickTimePeriodOptionCheckbox } from '@/utils/search/searchUtils';
import { SearchCaseRequest } from '@/types/types';
import { searchCases } from '@/requests/searches.js';
import { validateForm, resetForm, dialogBeforeClose } from '@/utils/functions';

interface ElSelectListItem {
	value: string;
	label: string;
}

interface SearchCasesData {
	request: SearchCaseRequest;
	rules: Object;
	state: string;
	id: string;
	timePeriod: string | null;
	selectedTimePeriod: any[];
	filteredCases: ElSelectListItem[];
	casesLoading: boolean;
	waiting: boolean;
	caseId: string | null;
	showErr: boolean;
	errMsg: string;
}

export default defineComponent({
	name: 'DialogAssignToCase',
	data(): SearchCasesData {
		return {
			request: {
				searchTerm: '',
				states: [],
				fromDate: null,
				toDate: null,
			},
			rules: {
				searchTerm: [{ min: 3, message: texts.formErrors.minChars, trigger: 'blur' }],
			},
			state: 'active',
			id: 'assignToCaseForm',
			timePeriod: null,
			selectedTimePeriod: [],
			filteredCases: [],
			casesLoading: false,
			waiting: false,
			caseId: null,
			showErr: false,
			errMsg: texts.complaints.assignToCaseFailed,
		};
	},
	components: {
		TheForm,
		TheButton,
	},
	props: {
		open: {
			type: Boolean,
			required: true,
		},
		complaintId: {
			type: String,
			required: true,
		},
	},
	setup() {
		const formRef = ref<FormInstance | null>(null);

		return {
			formRef,
		};
	},
	emits: ['reload', 'update:open'],
	computed: {
		timePeriodOptions() {
			return returnFormOptions('timePeriod');
		},
		texts() {
			return texts;
		},
	},
	methods: {
		setCaseId(id) {
			if (id) this.caseId = String(id);
		},
		handleClickOptionWrapper(item) {
			const { newSelectedTimePeriod, newTimePeriodOptions } = handleClickTimePeriodOptionCheckbox(
				item,
				this.selectedTimePeriod,
				this.timePeriodOptions,
			);

			this.selectedTimePeriod = newSelectedTimePeriod;
			this.timePeriodOptions = newTimePeriodOptions;

			// set fromDate and toDate from timePeriod
			this.request.fromDate = null;
			this.request.toDate = null;

			const timeperiodVal = newSelectedTimePeriod[0];
			if (timeperiodVal) {
				const { fromDate, toDate } = getDateRangeFromTimePeriod(timeperiodVal);
				this.request.fromDate = fromDate;
				this.request.toDate = toDate;
			}
		},
		focusRemoteSearchSelect() {
			if (!this.caseId) {
				this.request.searchTerm = '';
			}
		},
		async doSearch(val) {
			if (!val) return;

			this.filteredCases = [];
			this.casesLoading = true;
			this.request.searchTerm = val;

			if (this.state === 'active') {
				this.request.states = [
					CaseState.NoStatus,
					CaseState.ExaminationSmallSenate,
					CaseState.ExaminationSmallSenateRunning,
					CaseState.Statements,
					CaseState.ExaminationWerberat,
					CaseState.ExaminationWerberatRunning,
				];
			} else if (this.state === 'done') {
				this.request.states = [
					CaseState.DecisionSensitization,
					CaseState.DecisionNoReason,
					CaseState.ImmediateSubjectWithdrawal,
					CaseState.NotResponsible,
					CaseState.ProcedureNotPossible,
					CaseState.Unfounded,
					CaseState.DecisionStop,
				];
			}

			let searchRequest = prepareSearchRequest(this.request) as SearchCaseRequest;
			const searchResult = await searchCases(searchRequest, 0);
			if (searchResult.success) {
				// @ts-ignore
				this.filteredCases = searchResult.data.items.map((item) => {
					return {
						value: String(item.id),
						label: String(item.title),
					};
				});
			}

			this.casesLoading = false;
		},
		async save() {
			this.waiting = true;

			const requestAssignToCase = {
				complaintId: String(this.complaintId),
				caseId: String(this.caseId),
			} as AssignCaseToComplaintRequest;

			const result = await assignCaseToComplaint(requestAssignToCase);
			if (result.success) {
				this.handleClose(true);
			} else {
				const msg = translateToGerman(result.error);
				if (msg) {
					this.errMsg = msg;
				}

				this.showErr = true;
			}

			this.waiting = false;
		},
		handleClose(reload: Boolean) {
			resetForm(this.formRef, this.id);

			this.state = 'active';
			this.timePeriod = null;

			this.showErr = false;

			if (reload) {
				this.$emit('reload');
			}
			this.$emit('update:open', false);
		},
		dialogBeforeClose,
	},
});
</script>

<style lang="scss"></style>
